import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useSchool } from '../contexts/SchoolContext';
import Spinner from '../components/Spinner';

const getTermEndDate = (startDate?: string, endDate?: string, durationMonths?: number) => {
  if (endDate) {
    const result = new Date(endDate);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  const monthCount = Number(durationMonths ?? 0);
  if (startDate && Number.isFinite(monthCount) && monthCount > 0) {
    const result = new Date(startDate);
    result.setHours(0, 0, 0, 0);
    result.setMonth(result.getMonth() + monthCount);
    result.setHours(23, 59, 59, 999);
    return result;
  }
  return null;
};

const isTermEnded = (startDate?: string, endDate?: string, durationMonths?: number) => {
  const end = getTermEndDate(startDate, endDate, durationMonths);
  return end ? Date.now() > end.getTime() : false;
};

const SchoolTermHistory: React.FC = () => {
  const navigate = useNavigate();
  const { school, loading } = useSchool();
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [termNames, setTermNames] = useState<string[]>([]);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedClass, setSelectedClass] = useState('');

  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!school?._id) return;
      setLoadingResults(true);
      setError(null);
      try {
        const termQuery = selectedTerm ? `?termName=${encodeURIComponent(selectedTerm)}` : '';
        const response = await api.get(`/schools/${school._id}/term-history${termQuery}`);
        const data = response.data || {};
        setSubmissions(Array.isArray(data.submissions) ? data.submissions : []);
        setTermNames(Array.isArray(data.termNames) ? data.termNames : []);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load term history');
      } finally {
        setLoadingResults(false);
      }
    };

    fetchSubmissions();
  }, [school?._id, selectedTerm]);

  const submissionSummary = useMemo(() => {
    const teachers = new Map<string, { classes: Set<string>; studentIds: Set<string>; eligibleCount: number; totalStudents: number }>();
    const classes = new Set<string>();

    submissions.forEach((submission) => {
      const teacherName = submission.staff?.name || 'Unknown';
      const className = submission.className || submission.student?.className || 'Unknown';
      const studentId = submission.student?._id || submission.student;
      classes.add(className);

      if (!teachers.has(teacherName)) {
        teachers.set(teacherName, {
          classes: new Set(),
          studentIds: new Set(),
          eligibleCount: 0,
          totalStudents: 0
        });
      }

      const teacherData = teachers.get(teacherName)!;
      teacherData.classes.add(className);
      if (studentId) teacherData.studentIds.add(studentId.toString());
      teacherData.totalStudents += 1;
      if (submission.isEligibleForExam) {
        teacherData.eligibleCount += 1;
      }
    });

    const teacherCards = Array.from(teachers.entries()).map(([teacherName, teacherData]) => ({
      teacherName,
      classCount: teacherData.classes.size,
      studentCount: teacherData.studentIds.size,
      eligibleCount: teacherData.eligibleCount
    }));

    return {
      teacherCards,
      classCount: classes.size,
      teacherCount: teacherCards.length
    };
  }, [submissions]);

  const teacherOptions = useMemo(() => {
    const teachers = new Set<string>();
    submissions.forEach((submission) => {
      teachers.add(submission.staff?.name || 'Unknown');
    });
    return Array.from(teachers).sort();
  }, [submissions]);

  const classOptions = useMemo(() => {
    const classes = new Set<string>();
    submissions.forEach((submission) => {
      classes.add(submission.className || submission.student?.className || 'Unknown');
    });
    return Array.from(classes).sort();
  }, [submissions]);

  const filteredSubmissions = useMemo(() => {
    return submissions.filter((submission) => {
      const teacherName = submission.staff?.name || 'Unknown';
      const className = submission.className || submission.student?.className || 'Unknown';

      if (selectedTeacher && teacherName !== selectedTeacher) {
        return false;
      }
      if (selectedClass && className !== selectedClass) {
        return false;
      }
      return true;
    });
  }, [submissions, selectedTeacher, selectedClass]);

  const classGroups = useMemo(() => {
    const map = new Map<string, { studentCount: number; eligibleCount: number }>();
    filteredSubmissions.forEach((submission) => {
      if (!selectedTeacher) return;
      const className = submission.className || submission.student?.className || 'Unknown';
      const current = map.get(className) || { studentCount: 0, eligibleCount: 0 };
      current.studentCount += 1;
      if (submission.isEligibleForExam) current.eligibleCount += 1;
      map.set(className, current);
    });
    return Array.from(map.entries()).map(([className, stats]) => ({ className, ...stats }));
  }, [filteredSubmissions, selectedTeacher]);

  const clearTeacherFilter = () => setSelectedTeacher('');
  const clearClassFilter = () => setSelectedClass('');
  const clearAllFilters = () => {
    setSelectedTeacher('');
    setSelectedClass('');
  };

  const activeTermName = school?.activeTermName || '';
  const activeTermEnded = isTermEnded(school?.activeTermStartDate, school?.activeTermEndDate, school?.termDurationMonths);
  const activeTermEndText = activeTermEnded ? 'Term has ended' : activeTermName ? `Available after ${getTermEndDate(school?.activeTermStartDate, school?.activeTermEndDate, school?.termDurationMonths)?.toLocaleDateString()}` : 'Pending term end date configuration';
  const selectedActiveTerm = selectedTerm === activeTermName && activeTermName;
  const showPendingTermInfo = selectedActiveTerm && !activeTermEnded;
  const showEarlyHistoryInfo = !activeTermEnded && !selectedTerm && !termNames.length && !submissions.length;

  const studentRows = useMemo(() => {
    if (!selectedTeacher || !selectedClass) return [];
    return filteredSubmissions.map((submission) => ({
      id: submission._id,
      studentId: submission.student?._id || submission.student,
      name: submission.student?.name || 'Unknown',
      className: submission.className || submission.student?.className || 'Unknown',
      teacher: submission.staff?.name || 'Unknown',
      termName: submission.termName || 'Unknown Term',
      quizScore: `${submission.quizScoreObtained || 0} / ${submission.quizScoreTotal || 0}`,
      assignmentScore: `${submission.assignmentScoreObtained || 0} / ${submission.assignmentScoreTotal || 0}`,
      attendance: Number(submission.attendancePercentage ?? 0).toFixed(2),
      eligible: submission.isEligibleForExam ? 'Yes' : 'No'
    }));
  }, [filteredSubmissions, selectedTeacher, selectedClass]);

  if (loading || loadingResults) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Spinner />
      </Box>
    );
  }

  const termName = selectedTerm || 'All Terms';
  const teacherCount = submissionSummary.teacherCount;
  const classCount = submissionSummary.classCount;
  const eligibleCount = filteredSubmissions.filter((item) => item.isEligibleForExam).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Term History
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/school/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Term History
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {termName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Term Duration
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {school?.termDurationMonths ? `${school.termDurationMonths} month${school.termDurationMonths === 1 ? '' : 's'}` : 'Not configured'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Active Term
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {school?.activeTermName || 'Not set'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Submitted Records
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {submissions.length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Teachers Submitted
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {teacherCount}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Classes Covered
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {classCount}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Eligible Records
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {eligibleCount}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
          <FormControl fullWidth sx={{ minWidth: 220 }}>
            <InputLabel id="filter-term-label">Term</InputLabel>
            <Select
              labelId="filter-term-label"
              label="Term"
              value={selectedTerm}
              onChange={(event) => {
                setSelectedTerm(event.target.value);
                setSelectedTeacher('');
                setSelectedClass('');
              }}
            >
              <MenuItem value="">All Terms</MenuItem>
              {termNames.map((termNameItem) => (
                <MenuItem key={termNameItem} value={termNameItem}>
                  {termNameItem}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ minWidth: 220 }}>
            <InputLabel id="filter-teacher-label">Filter by Teacher</InputLabel>
            <Select
              labelId="filter-teacher-label"
              label="Filter by Teacher"
              value={selectedTeacher}
              onChange={(event) => setSelectedTeacher(event.target.value)}
            >
              <MenuItem value="">All Teachers</MenuItem>
              {teacherOptions.map((teacher) => (
                <MenuItem key={teacher} value={teacher}>
                  {teacher}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ minWidth: 220 }}>
            <InputLabel id="filter-class-label">Filter by Class</InputLabel>
            <Select
              labelId="filter-class-label"
              label="Filter by Class"
              value={selectedClass}
              onChange={(event) => setSelectedClass(event.target.value)}
            >
              <MenuItem value="">All Classes</MenuItem>
              {classOptions.map((className) => (
                <MenuItem key={className} value={className}>
                  {className}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={clearTeacherFilter} disabled={!selectedTeacher}>
              Clear Teacher
            </Button>
            <Button variant="outlined" onClick={clearClassFilter} disabled={!selectedClass}>
              Clear Class
            </Button>
            <Button variant="contained" onClick={clearAllFilters} disabled={!selectedTeacher && !selectedClass}>
              Clear All
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {showPendingTermInfo && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Term history for the active term is available only after the current term ends. {activeTermEndText}
        </Alert>
      )}
      {showEarlyHistoryInfo && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Term history is available after the current term ends. Completed term records will appear here once the term closes.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {submissions.length === 0 ? (
        <Alert severity="info">No term history records found for the selected term.</Alert>
      ) : (
        <>
          {selectedTeacher === '' ? (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {submissionSummary.teacherCards.map((teacher) => (
                <Grid key={teacher.teacherName} item xs={12} sm={6} md={4}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {teacher.teacherName}
                      </Typography>
                      <Typography color="text.secondary">Classes: {teacher.classCount}</Typography>
                      <Typography color="text.secondary">Students: {teacher.studentCount}</Typography>
                      <Typography color="text.secondary">Eligible: {teacher.eligibleCount}</Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={() => {
                        setSelectedTeacher(teacher.teacherName);
                        setSelectedClass('');
                      }}>
                        View Classes
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : selectedClass === '' ? (
            <Box sx={{ mb: 4 }}>
              <Button variant="outlined" sx={{ mb: 3 }} onClick={() => setSelectedTeacher('')}>
                Back to Teachers
              </Button>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Classes for {selectedTeacher}
              </Typography>
              <Grid container spacing={3}>
                {classGroups.map((classGroup) => (
                  <Grid key={classGroup.className} item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {classGroup.className}
                        </Typography>
                        <Typography color="text.secondary">Students: {classGroup.studentCount}</Typography>
                        <Typography color="text.secondary">Eligible: {classGroup.eligibleCount}</Typography>
                      </CardContent>
                      <CardActions>
                        <Button size="small" onClick={() => setSelectedClass(classGroup.className)}>
                          View Students
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Button variant="outlined" onClick={() => setSelectedClass('')}>
                  Back to Classes
                </Button>
                <Button variant="outlined" onClick={() => setSelectedTeacher('')}>
                  Back to Teachers
                </Button>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {selectedTeacher} / {selectedClass}
                </Typography>
              </Stack>
              {studentRows.length === 0 ? (
                <Alert severity="info">No students found for this class and teacher.</Alert>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Student</TableCell>
                        <TableCell>Term</TableCell>
                        <TableCell>Quiz Score</TableCell>
                        <TableCell>Assignment Score</TableCell>
                        <TableCell>Attendance %</TableCell>
                        <TableCell>Eligible</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {studentRows.map((student) => (
                        <TableRow key={student.id} hover>
                          <TableCell>{student.name}</TableCell>
                          <TableCell>{student.termName}</TableCell>
                          <TableCell>{student.quizScore}</TableCell>
                          <TableCell>{student.assignmentScore}</TableCell>
                          <TableCell>{student.attendance}%</TableCell>
                          <TableCell>{student.eligible}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SchoolTermHistory;
